import { axialHexDistance } from '../../hexmap/coords';
import { getHexTileMapKey } from '../../hexmap/model/keys';
import type { HexTileMapKey } from '../../hexmap/model/types';
import { pathTo, reachableFrom } from '../../hexmap/pathfinding';
import { tickDerived } from '../derived';
import {
  addHeading,
  deployUnit,
  forget,
  type Occupancy,
  stopKind,
  storageCapacity,
  unitsByMint,
} from '../occupancy';
import { redirect } from '../redirect';
import type { Cell, EntityId, PlayerId, SimContext, SimState, SystemFn, Unit } from '../types';
import { unitMaxHp } from '../veterancy';
import { canHeal, healsKind } from './heal';
import { isStarving } from './upkeep';

/** How far (hexes) a healer looks for a wounded own unit to tend. */
const TEND_RADIUS = 6;
/**
 * How far (hexes) from its leader a group's follower looks: closer than
 * TEND_RADIUS, so it never strays from the army to a straggler and dies alone.
 */
const TEND_LEASH = 4;
/** Ticks between tending passes (0.5 s): healing itself runs every tick (heal.ts). */
const TEND_TICKS = 5;

/** The share of its max hp `u` is missing (0: unhurt). */
function missingShare(ctx: SimContext, u: Unit): number {
  const max = unitMaxHp(ctx.rules, u);
  return max > 0 ? Math.max(0, 1 - u.hp / max) : 0;
}

/**
 * The leader `healer` follows: its group's leader when that's another unit
 * outside a building, else none.
 */
function leaderOf(state: SimState, healer: Unit): Unit | undefined {
  const id = healer.groupId !== undefined ? state.groups?.[healer.groupId]?.leader : undefined;
  const leader = id !== undefined && id !== healer.id ? state.units[id] : undefined;
  return leader && !leader.inside ? leader : undefined;
}

/**
 * Whether `patient` is one `healer` may tend from `anchor`: another own unit
 * of the kind it heals, outside buildings, hurt, within `radius` of
 * `anchor`. Own units only, so tending never reveals what's under the fog.
 */
function tendable(
  ctx: SimContext,
  healer: Unit,
  patient: Unit | undefined,
  anchor: Cell,
  radius: number
): boolean {
  return (
    !!patient &&
    patient.id !== healer.id &&
    patient.owner === healer.owner &&
    !patient.inside &&
    healsKind(ctx.rules, healer, patient) &&
    missingShare(ctx, patient) > 0 &&
    axialHexDistance(anchor, patient.pos) <= radius
  );
}

/**
 * Who `healer` tends next: among the units it may tend from `anchor` within
 * `radius` (and not in `exclude`), the one missing the largest share of its
 * max hp, then a member of its own group, then the nearest, then the oldest.
 */
function tendTarget(
  ctx: SimContext,
  healer: Unit,
  anchor: Cell,
  units: readonly Unit[],
  exclude?: ReadonlySet<EntityId>,
  radius = TEND_RADIUS
): Unit | undefined {
  let best: Unit | undefined;
  let bestMate = false;
  let bestShare = 0;
  let bestDist = 0;
  // `units` is in mint order, so a full tie keeps the older.
  for (const u of units) {
    if (exclude?.has(u.id) || !tendable(ctx, healer, u, anchor, radius)) continue;
    const mate = healer.groupId !== undefined && u.groupId === healer.groupId;
    const share = missingShare(ctx, u);
    const dist = axialHexDistance(healer.pos, u.pos);
    const wins =
      !best ||
      (share !== bestShare ? share > bestShare : mate !== bestMate ? mate : dist < bestDist);
    if (!wins) continue;
    best = u;
    bestMate = mate;
    bestShare = share;
    bestDist = dist;
  }
  return best;
}

/** Stops `unit` where it is (on the nearer end of a step under way). */
function stand(state: SimState, unit: Unit): void {
  redirect(state, unit, [unit.pos]);
}

/**
 * Steers `healer` into heal range of `patient`: it stops once in range on a
 * tile it can stand on, keeps going while its path ends in range, else heads
 * for the nearest free open tile in range (by walking distance, around what
 * its owner can see in the way, and never into `rival` territory its owner
 * sees unless it already stands in it or its `leader` does: a group's healer
 * goes where its army fights). Nothing reachable in range: it stays put.
 * Returns whether it's in range or on its way.
 */
function approach(
  state: SimState,
  ctx: SimContext,
  occ: Occupancy,
  blocked: ReadonlySet<HexTileMapKey>,
  rival: ReadonlySet<HexTileMapKey>,
  healer: Unit,
  patient: Unit,
  leader: Unit | undefined
): boolean {
  const def = ctx.rules.units[healer.type];
  if (!def) return false;
  const range = def.range;
  const who = { id: healer.id, owner: healer.owner, type: healer.type };
  const canStand = (cell: Cell, reserve: boolean) =>
    stopKind(ctx.rules, occ, who, cell, reserve) === 'open';
  const inRange = (cell: Cell) => axialHexDistance(cell, patient.pos) <= range;
  if (inRange(healer.pos) && canStand(healer.pos, false)) {
    stand(state, healer);
    return true;
  }
  const end = healer.path?.[healer.path.length - 1];
  if (end && inRange(end) && canStand(end, true)) return true;
  let spot: Cell | undefined;
  const outside =
    !rival.has(getHexTileMapKey(healer.pos)) &&
    !(leader && rival.has(getHexTileMapKey(leader.pos)));
  const reach = reachableFrom(
    ctx.terrain,
    healer.pos,
    def.terrain,
    blocked,
    (cell) => {
      if (!inRange(cell) || !canStand(cell, true)) return false;
      spot = cell;
      return true;
    },
    outside && rival.size > 0 ? (q, r) => !rival.has(`${q},${r}` as HexTileMapKey) : undefined
  );
  if (!spot) return false;
  forget(occ, healer);
  redirect(state, healer, pathTo(reach, spot));
  addHeading(occ, healer, spot);
  return true;
}

/**
 * Healers tend the wounded without being told: every TEND_TICKS, each
 * healer (oldest first) that isn't on another order, automated, or walking
 * a move of its own takes a `heal` order on the own unit that needs it most
 * within TEND_RADIUS (see `tendTarget`; a group's follower looks only within
 * TEND_LEASH of its leader, so it stays with the army; a healer leading its group tends only
 * its group-mates, so it never drags the group off to outsiders; anyone else
 * looks around the tile it took up tending from, its order's `post`) and
 * walks into heal range of it (`approach`; one it can't get into range of is
 * passed over for the next). It keeps that patient until it's
 * healed, gone, inside a building or out of reach of its anchor, then picks
 * the next, and with nobody left drops the order and stands (a follower then
 * rejoins its formation). It never walks into rival territory its owner sees
 * to reach a patient (`approach`), so it isn't drawn under a city's fire. One whose owner is starving mends nobody (heal.ts),
 * so it drops its heal order and stands too. heal.ts does the mending, every
 * tick. A healer stored in a building (a barracks) comes out when a unit it
 * would tend, and no other healer is tending, stands within TEND_RADIUS of
 * it (one healer per patient). A move order replaces the heal order. Runs
 * after formation (which leaves healers on a heal order alone) and before
 * movement.
 */
const tend: SystemFn = (state, ctx) => {
  if (state.tick % TEND_TICKS !== 0) return;
  const { rules } = ctx;
  const units = unitsByMint(state);
  const healers = units.filter((u) => canHeal(rules, u) && !u.auto);
  if (healers.length === 0) return;
  const starving = new Map<PlayerId, boolean>();
  const starves = (owner: PlayerId) => {
    let s = starving.get(owner);
    if (s === undefined) {
      s = isStarving(state, rules, owner);
      starving.set(owner, s);
    }
    return s;
  };
  // Patients a healer is already on, so stored healers don't all come out for one.
  const tended = new Set<EntityId>();
  for (const h of healers) if (!h.inside && h.order?.kind === 'heal') tended.add(h.order.targetId);
  const derived = tickDerived(state, ctx);
  let occ: Occupancy | undefined;
  const occupancy = () => {
    occ ??= derived.occupancy();
    return occ;
  };
  // Owner → rival territory tiles it sees (fog-safe), for `approach`.
  const rivalTiles = new Map<PlayerId, Set<HexTileMapKey>>();
  const rivalOf = (owner: PlayerId) => {
    let tiles = rivalTiles.get(owner);
    if (!tiles) {
      tiles = new Set();
      const vision = derived.vision(owner);
      for (const [key, cityId] of derived.owners()) {
        const city = state.buildings[cityId];
        if (city && city.owner !== owner && vision.has(key)) tiles.add(key);
      }
      rivalTiles.set(owner, tiles);
    }
    return tiles;
  };

  for (const healer of healers) {
    if (starves(healer.owner)) {
      // Starving healers mend nobody (heal.ts): drop the order so a follower rejoins its formation.
      if (healer.order?.kind === 'heal') {
        delete healer.order;
        stand(state, healer);
      }
      continue;
    }
    if (healer.inside) {
      const store = state.buildings[healer.inside];
      if (!store || storageCapacity(rules, store) === 0) continue;
      const patient = tendTarget(ctx, healer, store.pos, units, tended);
      if (!patient || !deployUnit(state, ctx, occupancy(), healer)) continue;
      healer.order = { kind: 'heal', targetId: patient.id, post: { ...store.pos } };
      tended.add(patient.id);
      continue;
    }
    const order = healer.order;
    if (order && order.kind !== 'heal') continue;
    const leader = leaderOf(state, healer);
    // Walking a move of its own (a follower's path is the formation's).
    if (!order && !leader && healer.path && healer.path.length > 0) continue;
    const group = healer.groupId;
    const leads = group !== undefined && state.groups?.[group]?.leader === healer.id;
    const pool = leads ? units.filter((u) => u.groupId === group) : units;
    // A follower has no post: it looks around its leader wherever it goes.
    const post = leader ? undefined : (order?.post ?? { q: healer.pos.q, r: healer.pos.r });
    const anchor = leader?.pos ?? post ?? healer.pos;
    const radius = leader ? TEND_LEASH : TEND_RADIUS;
    const current = order ? state.units[order.targetId] : undefined;
    const keep =
      tendable(ctx, healer, current, anchor, radius) && (!leads || current?.groupId === group);
    // A patient it can't get into range of (e.g. deep in rival territory it
    // won't enter) is passed over for the next, so it never stalls on one.
    const tried = new Set<EntityId>();
    let patient = keep ? current : tendTarget(ctx, healer, anchor, pool, undefined, radius);
    let ok = false;
    while (patient) {
      healer.order = post
        ? { kind: 'heal', targetId: patient.id, post }
        : { kind: 'heal', targetId: patient.id };
      ok = approach(
        state,
        ctx,
        occupancy(),
        derived.blockers(healer.owner),
        rivalOf(healer.owner),
        healer,
        patient,
        leader
      );
      if (ok) break;
      tried.add(patient.id);
      patient = tendTarget(ctx, healer, anchor, pool, tried, radius);
    }
    if (!ok || !patient) {
      if (healer.order?.kind === 'heal') {
        delete healer.order;
        stand(state, healer);
      }
      continue;
    }
    tended.add(patient.id);
  }
};

export { TEND_LEASH, TEND_RADIUS, TEND_TICKS, tend, tendTarget };
